import { formatListWithOxfordComma } from "@/utils/formatList";
import pluralize from "@/utils/plural";
import { useState } from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { CoachInfoSubcomponentProps } from "../CoachInfo";

export default function ExperienceAndSkills({
    coachInfoData,
}: CoachInfoSubcomponentProps) {
    const [isOpen, setIsOpen] = useState(false);

    const coachingExperience: { title: string; description: string }[] = [];

    if (coachInfoData?.number_of_clients_coached) {
        coachingExperience.push({
            title: "Coached",
            description: `${coachInfoData.number_of_clients_coached}+ clients`,
        });
    }

    coachingExperience.push({
        title: "Across these industries",
        description: formatListWithOxfordComma(
            coachInfoData?.coach.industries.map(
                (industry) => industry.pretty_name,
            ) ?? [],
        ),
    });

    if (
        coachInfoData?.uniqueJobTitlesCoached &&
        coachInfoData?.uniqueJobTitlesCoached.length > 0
    ) {
        coachingExperience.push({
            title: "Clients included",
            description: formatListWithOxfordComma(
                coachInfoData?.uniqueJobTitlesCoached ?? [],
            ),
        });
    }

    coachingExperience.push({
        title: "Languages",
        description: formatListWithOxfordComma(
            coachInfoData?.coach.languages.map((language) => language.name) ??
                [],
        ),
    });

    const careerSkills =
        coachInfoData?.coach.life_areas
            .filter((area) => area.used_in_deep_career && area.score !== 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, 8)
            .map((area) => area.expertise_tag) ?? [];

    const lifeSkills =
        coachInfoData?.coach.life_areas
            .filter((area) => !area.used_in_deep_career && area.score !== 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, 8)
            .map((area) => area.expertise_tag) ?? [];

    return !coachInfoData ? (
        <ExperienceAndSkillsSkeleton />
    ) : (
        <div className="flex flex-col gap-2">
            <button
                className="flex items-center gap-2 font-bold text-xl"
                onClick={() => setIsOpen(!isOpen)}
            >
                Experience & Skills
                {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
            </button>

            {isOpen && (
                <>
                    <div className="font-bold">Coaching experience</div>
                    <ul className="text-brand-gray">
                        {coachingExperience.map((experience) => (
                            <li
                                key={experience.title}
                                className="ml-8 list-disc"
                            >
                                <span className="font-medium">
                                    {experience.title}:
                                </span>{" "}
                                {experience.description}
                            </li>
                        ))}
                    </ul>

                    <div className="font-bold">Professional background</div>
                    <div className="text-brand-gray text-sm text-base">
                        Although a similar role/industry can bring relevant
                        context and awareness of challenges, it isn't necessary
                        for coaching success.
                    </div>

                    <ul className="text-brand-gray">
                        {coachInfoData?.coach.professional_positions
                            .sort((a, b) => a.order - b.order)
                            .map((position) => (
                                <li
                                    key={position.id}
                                    className="ml-8 list-disc"
                                >
                                    <span className="font-medium">
                                        {position.job_title}
                                    </span>{" "}
                                    ({position.industry.pretty_name},{" "}
                                    {pluralize(position.years_at_role, "year")})
                                </li>
                            ))}
                    </ul>

                    {careerSkills.length > 0 && (
                        <>
                            <div className="font-bold">
                                Career skills expertise
                            </div>
                            <PillItems items={careerSkills} />
                        </>
                    )}

                    {lifeSkills.length > 0 && (
                        <>
                            <div className="font-bold">
                                Life skills expertise
                            </div>
                            <PillItems items={lifeSkills} />
                        </>
                    )}
                </>
            )}
        </div>
    );
}

function PillItems({ items }: { items: string[] }) {
    return (
        <div className="flex flex-wrap gap-x-2 gap-y-2 self-stretch p-2">
            {items.map((item) => (
                <div
                    key={item}
                    className="rounded-full bg-brand-lightGray px-3 py-1 text-xs font-medium uppercase text-brand-black"
                >
                    {item}
                </div>
            ))}
        </div>
    );
}

function ExperienceAndSkillsSkeleton() {
    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 font-bold text-xl text-brand-gray animate-pulse">
                Experience & Skills
                <IoIosArrowDown />
            </div>
        </div>
    );
}
